/**
 * Validaciones mínimas del contrato Sharma (tablas YTD, drawdowns, risk attribution).
 * Errores explícitos para no mostrar tablas/gráficas “rellenas” cuando el JSON no cumple.
 */

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** @param {unknown} s */
export function isValidReportPeriodYyyyMm(s) {
  if (s == null || typeof s !== "string") return false;
  const t = s.trim();
  if (!/^\d{6}$/.test(t)) return false;
  const mo = parseInt(t.slice(4, 6), 10);
  return mo >= 1 && mo <= 12;
}

/**
 * Shape Sharma: `strategies` como mapa objeto (no array).
 * @param {object} u — ya `unwrapEnvelope`
 */
export function assertYtdStrategySharmaMapShape(u) {
  if (!u.strategies || typeof u.strategies !== "object" || Array.isArray(u.strategies)) {
    throw new Error(
      "ytd_strategy: se esperaba un objeto `strategies` (mapa por nombre de estrategia)."
    );
  }
  const entries = Object.entries(u.strategies);
  for (const [name, payload] of entries) {
    if (!payload || typeof payload !== "object") {
      throw new Error(`ytd_strategy: payload inválido para la estrategia "${name}".`);
    }
    const ytd = payload.ytd && typeof payload.ytd === "object" ? payload.ytd : null;
    const l3 =
      payload.last_3_months && typeof payload.last_3_months === "object"
        ? payload.last_3_months
        : null;
    const hasYtdNumber =
      ytd &&
      (num(ytd.performance) != null ||
        num(ytd.stdev) != null ||
        num(ytd.sharpe_ratio) != null);
    const hasL3 =
      l3 &&
      Object.keys(l3).length > 0 &&
      Object.values(l3).some((v) => num(v) != null);
    if (!hasYtdNumber && !hasL3) {
      throw new Error(
        `ytd_strategy: la estrategia "${name}" no incluye métricas YTD ni last_3_months reconocibles.`
      );
    }
  }
}

/**
 * Shape Sharma: `indexes` array con index_name o index_id y métricas.
 * @param {object} u — ya `unwrapEnvelope`
 */
export function assertYtdIndexIndexesShape(u) {
  if (!Array.isArray(u.indexes) || u.indexes.length < 1) {
    throw new Error("ytd_index: se esperaba un array `indexes` con al menos un elemento.");
  }
  for (let i = 0; i < u.indexes.length; i++) {
    const item = u.indexes[i];
    if (!item || typeof item !== "object") {
      throw new Error(`ytd_index: elemento de índice inválido en la posición ${i}.`);
    }
    const nameOk =
      item.index_name != null && String(item.index_name).trim() !== "";
    const idOk = item.index_id != null && item.index_id !== "";
    if (!nameOk && !idOk) {
      throw new Error(
        `ytd_index: falta index_name o index_id reconocible en la posición ${i}.`
      );
    }
    const ytd = item.ytd && typeof item.ytd === "object" ? item.ytd : null;
    const l3 =
      item.last_3_months && typeof item.last_3_months === "object"
        ? item.last_3_months
        : null;
    const hasYtdNumber =
      ytd &&
      (num(ytd.performance) != null ||
        num(ytd.stdev) != null ||
        num(ytd.sharpe_ratio) != null);
    const hasL3 =
      l3 &&
      Object.keys(l3).length > 0 &&
      Object.values(l3).some((v) => num(v) != null);
    if (!hasYtdNumber && !hasL3) {
      throw new Error(
        `ytd_index: el índice en la posición ${i} no incluye métricas YTD ni last_3_months reconocibles.`
      );
    }
  }
}

/** @param {unknown[]} rowsRaw */
export function assertYtdRowsArrayShape(rowsRaw) {
  if (!Array.isArray(rowsRaw) || rowsRaw.length < 1) {
    throw new Error("ytd: la respuesta no contiene filas de tabla reconocibles.");
  }
  const first = rowsRaw[0];
  if (!first || typeof first !== "object") {
    throw new Error("ytd: primera fila inválida o vacía.");
  }
  const fundLike =
    first.fund ??
    first.Fund ??
    first.fundName ??
    first.name ??
    first.Name;
  if (fundLike == null || String(fundLike).trim() === "") {
    throw new Error(
      "ytd: las filas no incluyen identificador de fondo/índice (fund/name) reconocible."
    );
  }
}

/**
 * Mapa `drawdowns` tipo Sharma (YYYYMM → número).
 * @param {object} ddMap
 */
export function assertDrawdownsMapShape(ddMap) {
  if (!ddMap || typeof ddMap !== "object" || Array.isArray(ddMap)) {
    throw new Error("drawdowns: se esperaba un objeto `drawdowns` con series por periodo.");
  }
  const keys = Object.keys(ddMap);
  if (keys.length < 1) {
    throw new Error("drawdowns: `drawdowns` no contiene ningún periodo.");
  }
  let numericCount = 0;
  for (const k of keys) {
    if (num(ddMap[k]) != null) numericCount += 1;
  }
  if (numericCount < 1) {
    throw new Error("drawdowns: ningún valor numérico reconocible en la serie de drawdowns.");
  }
}

/**
 * @param {string[]} categories
 * @param {number[]} data
 */
export function assertChartSeriesLengths(categories, data) {
  if (!Array.isArray(categories) || categories.length < 1) {
    throw new Error("drawdowns: categorías vacías o no reconocibles.");
  }
  if (!Array.isArray(data) || data.length < 1) {
    throw new Error("drawdowns: serie de datos vacía o no reconocible.");
  }
  if (categories.length !== data.length) {
    throw new Error(
      "drawdowns: el número de categorías no coincide con el de puntos de la serie."
    );
  }
  const anyFinite = data.some((v) => Number.isFinite(Number(v)));
  if (!anyFinite) {
    throw new Error("drawdowns: la serie no contiene valores numéricos finitos.");
  }
}

/**
 * Estrategias tipo Sharma con allocation_pct / risk_pct / performance_pct.
 * @param {object[]} strategies
 */
export function assertRiskAttributionStrategiesShape(strategies) {
  if (!Array.isArray(strategies) || strategies.length < 1) {
    throw new Error("risk_attribution: se esperaba un array `strategies` no vacío.");
  }
  for (let i = 0; i < strategies.length; i++) {
    const s = strategies[i];
    if (!s || typeof s !== "object") {
      throw new Error(`risk_attribution: estrategia inválida en la posición ${i}.`);
    }
    const a = num(s.allocation_pct);
    const r = num(s.risk_pct);
    const p = num(s.performance_pct);
    if (a == null && r == null && p == null) {
      throw new Error(
        `risk_attribution: la estrategia en la posición ${i} no incluye allocation_pct, risk_pct ni performance_pct numéricos.`
      );
    }
  }
}

/**
 * Layout alternativo: 3 categorías y series column con data numérica.
 * @param {object} u — ya `unwrapEnvelope`
 */
/**
 * VaR: objeto raíz con `portfolio` y `funds` (array; puede estar vacío — manejo amigable en adaptador).
 * @param {object} u — ya `unwrapEnvelope`
 */
export function assertValueAtRiskEnvelopeShape(u) {
  if (!u || typeof u !== "object" || Array.isArray(u)) {
    throw new Error("value_at_risk: se esperaba un objeto raíz.");
  }
  if (!u.portfolio || typeof u.portfolio !== "object" || Array.isArray(u.portfolio)) {
    throw new Error("value_at_risk: falta el objeto `portfolio`.");
  }
  if (!Array.isArray(u.funds)) {
    throw new Error("value_at_risk: se esperaba un array `funds`.");
  }
}

/**
 * Business cycle scenario — GET `/api/risk-metrics/business-cycle-scenario`.
 * @param {object} block — escenario normalizado con `data[]`
 */
export function assertBusinessCycleScenarioShape(block) {
  if (!block || typeof block !== "object" || Array.isArray(block)) {
    throw new Error("business_cycle: se esperaba un objeto de escenario.");
  }
  if (!Array.isArray(block.data)) {
    throw new Error("business_cycle: se esperaba un array `data`.");
  }
}

/**
 * Marginal risk beta — GET `/api/risk-metrics/marginal-risk-beta`.
 * v1: tres escalares en la raíz. v2: `risk_factors[]` con beta_up/beta_down por benchmark.
 * El adaptador dibuja barras Up/Down agrupadas; `significance` / `pnl_impact` no están en el contrato actual.
 * @param {object} u — ya `unwrapEnvelope`
 */
export function assertMarginalRiskBetaShape(u) {
  if (!u || typeof u !== "object" || Array.isArray(u)) {
    throw new Error("marginal_risk_beta: se esperaba un objeto raíz.");
  }
  const up = num(u.beta_up);
  const down = num(u.beta_down);
  const undiff = num(u.beta_undiff);
  const hasScalars = up != null || down != null || undiff != null;
  const hasRiskFactors =
    Array.isArray(u.risk_factors) && u.risk_factors.length > 0;
  if (!hasScalars && !hasRiskFactors) {
    throw new Error(
      "marginal_risk_beta: se esperaban beta_up/beta_down/beta_undiff en raíz o un array risk_factors no vacío."
    );
  }
}

/**
 * Risk profile radar — GET `/api/risk-metrics/risk-profile`.
 * Contrato: objeto con exactamente las claves `"1"` … `"6"`; cada valor `{ name: string, value: number }`.
 * Orden de visualización: numérico 1 → 6 (orden de negocio del backend).
 * @param {object} u — ya `unwrapEnvelope`
 */
export function assertRiskProfileRadarShape(u) {
  if (!u || typeof u !== "object" || Array.isArray(u)) {
    throw new Error("risk_profile: se esperaba un objeto raíz.");
  }
  const keys = ["1", "2", "3", "4", "5", "6"];
  for (const k of keys) {
    const cell = u[k];
    if (!cell || typeof cell !== "object" || Array.isArray(cell)) {
      throw new Error(`risk_profile: falta o es inválida la entrada para la clave "${k}".`);
    }
    const v = num(cell.value ?? cell.Value);
    if (v == null) {
      throw new Error(`risk_profile: la clave "${k}" debe incluir value numérico finito.`);
    }
    const name = cell.name ?? cell.Name;
    if (name == null || String(name).trim() === "") {
      throw new Error(`risk_profile: la clave "${k}" debe incluir name no vacío.`);
    }
  }
}

export function assertRiskAttributionThreeCategorySeries(u) {
  if (!Array.isArray(u?.series) || !Array.isArray(u?.categories)) {
    throw new Error("risk_attribution: se esperaban `categories` y `series` en formato columnas.");
  }
  if (u.categories.length !== 3) {
    throw new Error("risk_attribution: se esperaban exactamente 3 categorías de métricas.");
  }
  if (u.series.length < 1) {
    throw new Error("risk_attribution: `series` está vacío.");
  }
  for (let i = 0; i < u.series.length; i++) {
    const s = u.series[i];
    const d = Array.isArray(s?.data) ? s.data : [];
    if (d.length !== 3) {
      throw new Error(
        `risk_attribution: la serie en la posición ${i} debe tener 3 valores (una por categoría).`
      );
    }
    const any = d.some((v) => num(v) != null);
    if (!any) {
      throw new Error(`risk_attribution: la serie en la posición ${i} no tiene valores numéricos.`);
    }
  }
}
